import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, Clock, AlertTriangle, DollarSign, Search, Download, ChevronDown, ChevronRight, Calendar, Users, TrendingDown } from 'lucide-react';
import { paymentAgeingAPI } from '../../services/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const BUCKET_CONFIG = [
  { key: 'bucket_0_30', label: '0-30 days', shortLabel: '0-30d', color: '#059669', bg: 'bg-emerald-50', text: 'text-emerald-700', bar: 'bg-emerald-500' },
  { key: 'bucket_31_60', label: '31-60 days', shortLabel: '31-60d', color: '#d97706', bg: 'bg-amber-50', text: 'text-amber-700', bar: 'bg-amber-500' },
  { key: 'bucket_61_90', label: '61-90 days', shortLabel: '61-90d', color: '#ea580c', bg: 'bg-orange-50', text: 'text-orange-700', bar: 'bg-orange-500' },
  { key: 'bucket_90_plus', label: '90+ days', shortLabel: '90+d', color: '#dc2626', bg: 'bg-red-50', text: 'text-red-700', bar: 'bg-red-500' },
];

const fmtINR = (v) => {
  if (!v || v === 0) return '-';
  if (v >= 10000000) return `${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(2)} L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)} K`;
  return v.toLocaleString('en-IN');
};

const fmtFullINR = (v) => {
  if (!v || v === 0) return '-';
  return v.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const PaymentAgeingReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('total_outstanding');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [bucketFilter, setBucketFilter] = useState('all');

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    setLoading(true);
    try {
      const result = await paymentAgeingAPI.getReport();
      setData(result);
    } catch (err) {
      console.error('Failed to load ageing report:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = [...data.customers];

    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(r =>
        r.customer_name.toLowerCase().includes(q) ||
        (r.salesperson_name || '').toLowerCase().includes(q) ||
        (r.state || '').toLowerCase().includes(q)
      );
    }

    if (bucketFilter !== 'all') {
      rows = rows.filter(r => {
        if (bucketFilter === 'overdue') return r.bucket_31_60 > 0 || r.bucket_61_90 > 0 || r.bucket_90_plus > 0;
        if (bucketFilter === '90+') return r.bucket_90_plus > 0;
        if (bucketFilter === '61-90') return r.bucket_61_90 > 0;
        if (bucketFilter === '31-60') return r.bucket_31_60 > 0;
        return true;
      });
    }

    rows.sort((a, b) => {
      if (sortBy === 'oldest_invoice_days') return (b.oldest_invoice_days || 0) - (a.oldest_invoice_days || 0);
      return (b[sortBy] || 0) - (a[sortBy] || 0);
    });

    return rows;
  }, [data, search, bucketFilter, sortBy]);

  const toggleExpand = (cid) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });
  };

  const exportToExcel = () => {
    if (!data) return;

    const customerRows = filtered.map(c => ({
      'Customer': c.customer_name,
      'Type': c.type || '',
      'State': c.state || '',
      'Salesperson': c.salesperson_name || '-',
      'Credit Days': c.credit_days || 30,
      'Credit Limit': c.credit_limit || 'No limit',
      'Total Invoiced': c.total_invoiced,
      'Total Received': c.total_received,
      'Total Outstanding': c.total_outstanding,
      '0-30 Days': c.bucket_0_30,
      '31-60 Days': c.bucket_31_60,
      '61-90 Days': c.bucket_61_90,
      '90+ Days': c.bucket_90_plus,
      'Oldest Invoice (Days)': c.oldest_invoice_days,
      'Overdue Invoices': c.overdue_count,
    }));

    customerRows.push({});
    customerRows.push({
      'Customer': 'TOTALS',
      'Total Outstanding': data.summary.total_outstanding,
      '0-30 Days': data.summary.bucket_0_30,
      '31-60 Days': data.summary.bucket_31_60,
      '61-90 Days': data.summary.bucket_61_90,
      '90+ Days': data.summary.bucket_90_plus,
    });

    const invoiceRows = [];
    for (const c of filtered) {
      for (const inv of c.invoices || []) {
        invoiceRows.push({
          'Customer': c.customer_name,
          'OC Number': inv.oc_number,
          'OC Date': inv.date,
          'OC Total': inv.total,
          'Outstanding': inv.outstanding,
          'Age (Days)': inv.age_days,
          'Bucket': inv.bucket,
          'Status': inv.status,
        });
      }
    }

    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.json_to_sheet(customerRows);
    ws1['!cols'] = Array(15).fill({ wch: 16 });
    XLSX.utils.book_append_sheet(wb, ws1, 'Ageing Summary');

    if (invoiceRows.length > 0) {
      const ws2 = XLSX.utils.json_to_sheet(invoiceRows);
      ws2['!cols'] = Array(8).fill({ wch: 16 });
      XLSX.utils.book_append_sheet(wb, ws2, 'Invoice Detail');
    }

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `Payment_Ageing_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-sm text-gray-500">Loading ageing report...</span>
      </div>
    );
  }

  if (!data) {
    return <p className="text-sm text-gray-500 text-center py-16">No data available</p>;
  }

  const { summary } = data;
  const overdueTotal = (summary.bucket_31_60 || 0) + (summary.bucket_61_90 || 0) + (summary.bucket_90_plus || 0);
  const maxBucket = Math.max(summary.bucket_0_30 || 0, summary.bucket_31_60 || 0, summary.bucket_61_90 || 0, summary.bucket_90_plus || 0, 1);

  return (
    <div data-testid="payment-ageing-report" className="space-y-4">
      {/* Header with date */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] text-gray-500">
          <Calendar className="w-3.5 h-3.5" />
          Report as on: <span className="font-semibold text-gray-700">{summary.report_date ? new Date(summary.report_date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Today'}</span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#24549c15' }}>
            <DollarSign className="w-4 h-4" style={{ color: '#24549c' }} />
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }} data-testid="total-outstanding">{fmtINR(summary.total_outstanding)}</p>
            <p className="text-[10px] text-gray-500">Total Outstanding</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-red-100 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-red-50">
            <AlertTriangle className="w-4 h-4 text-red-500" />
          </div>
          <div>
            <p className="text-lg font-bold text-red-600" style={{ fontFamily: 'DM Sans' }} data-testid="total-overdue">{fmtINR(overdueTotal)}</p>
            <p className="text-[10px] text-gray-500">Total Overdue (&gt;30d)</p>
          </div>
        </div>
        {BUCKET_CONFIG.map(b => (
          <div key={b.key} className={`bg-white rounded-xl border border-gray-100 p-4`}>
            <p className="text-sm font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{fmtINR(summary[b.key] || 0)}</p>
            <p className="text-[10px] text-gray-500">{b.label}</p>
            <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${b.bar}`}
                style={{ width: `${Math.max(2, ((summary[b.key] || 0) / maxBucket) * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>

      {/* Overdue Alert */}
      {summary.overdue_customers > 0 && (
        <div className="p-3 rounded-xl border border-amber-200 bg-amber-50/50 flex items-start gap-3" data-testid="overdue-alert">
          <TrendingDown className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-amber-800">
              {summary.overdue_customers} customer{summary.overdue_customers > 1 ? 's have' : ' has'} overdue invoices (&gt;30 days)
            </p>
            <p className="text-[10px] text-amber-600 mt-0.5">
              Total overdue amount: INR {fmtFullINR(overdueTotal)} | Worst bucket (90+): INR {fmtFullINR(summary.bucket_90_plus || 0)}
            </p>
          </div>
        </div>
      )}

      {/* Ageing Stacked Bar */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <h3 className="text-xs font-bold text-gray-900 mb-3">Ageing Distribution</h3>
        {summary.total_outstanding > 0 ? (
          <div className="flex h-8 rounded-lg overflow-hidden">
            {BUCKET_CONFIG.map(b => {
              const pct = ((summary[b.key] || 0) / summary.total_outstanding) * 100;
              if (pct < 0.5) return null;
              return (
                <div key={b.key} className={`${b.bar} flex items-center justify-center transition-all`}
                  style={{ width: `${pct}%`, minWidth: pct > 5 ? 'auto' : '0' }}
                  title={`${b.label}: INR ${fmtFullINR(summary[b.key])} (${pct.toFixed(1)}%)`}>
                  {pct > 8 && (
                    <span className="text-[10px] font-semibold text-white truncate px-1">
                      {b.shortLabel} {pct.toFixed(0)}%
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-gray-400 text-center py-2">No outstanding amounts</p>
        )}
        <div className="flex gap-4 mt-2.5">
          {BUCKET_CONFIG.map(b => (
            <div key={b.key} className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded-sm ${b.bar}`} />
              <span className="text-[10px] text-gray-500">{b.label}: <span className="font-semibold text-gray-700">{fmtINR(summary[b.key] || 0)}</span></span>
            </div>
          ))}
        </div>
      </div>

      {/* Filters & Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            data-testid="ageing-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search customer, salesperson, state..."
            className="w-full pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"
          />
        </div>
        <div className="flex gap-1.5">
          {[
            { id: 'all', label: 'All', count: data.customers.length },
            { id: 'overdue', label: 'Overdue', count: data.customers.filter(c => c.bucket_31_60 > 0 || c.bucket_61_90 > 0 || c.bucket_90_plus > 0).length },
            { id: '90+', label: '90+ Days', count: data.customers.filter(c => c.bucket_90_plus > 0).length },
            { id: '61-90', label: '61-90 Days', count: data.customers.filter(c => c.bucket_61_90 > 0).length },
            { id: '31-60', label: '31-60 Days', count: data.customers.filter(c => c.bucket_31_60 > 0).length },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setBucketFilter(f.id)}
              data-testid={`ageing-filter-${f.id}`}
              className={`px-3 py-1.5 text-[11px] font-medium rounded-full transition-all ${
                bucketFilter === f.id
                  ? f.id === 'overdue' || f.id === '90+' ? 'text-white bg-red-600' : 'text-white'
                  : f.id === 'overdue' || f.id === '90+' ? 'text-red-600 bg-red-50 hover:bg-red-100' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
              }`}
              style={bucketFilter === f.id && f.id !== 'overdue' && f.id !== '90+' ? { backgroundColor: '#24549c' } : {}}
            >
              {f.label} ({f.count})
            </button>
          ))}
        </div>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="px-3 py-2 text-xs border border-gray-200 rounded-lg"
          data-testid="ageing-sort"
        >
          <option value="total_outstanding">Sort: Outstanding</option>
          <option value="bucket_90_plus">Sort: 90+ Days</option>
          <option value="bucket_61_90">Sort: 61-90 Days</option>
          <option value="oldest_invoice_days">Sort: Oldest Invoice</option>
          <option value="overdue_count">Sort: Overdue Count</option>
        </select>
        <button
          onClick={exportToExcel}
          data-testid="export-ageing-excel"
          className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold text-white rounded-lg transition-all hover:opacity-90"
          style={{ backgroundColor: '#0d6b3e' }}
        >
          <Download className="w-3.5 h-3.5" />Export to Excel
        </button>
      </div>

      {/* Customer Stats */}
      <div className="flex items-center gap-3 text-[10px] text-gray-500">
        <Users className="w-3.5 h-3.5" />
        <span>Showing <span className="font-semibold text-gray-700">{filtered.length}</span> of {data.customers.length} customers with outstanding</span>
        <span className="text-gray-300">|</span>
        <span>Outstanding: <span className="font-semibold text-gray-700">INR {fmtFullINR(filtered.reduce((s, c) => s + c.total_outstanding, 0))}</span></span>
      </div>

      {/* Ageing Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="ageing-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="w-8 px-2 py-2.5"></th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Outstanding</th>
                {BUCKET_CONFIG.map(b => (
                  <th key={b.key} className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: b.color }}>
                    {b.shortLabel}
                  </th>
                ))}
                <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Oldest</th>
                <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Overdue</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-8 text-sm text-gray-400">No customers match your filters</td></tr>
              ) : (
                filtered.map(c => {
                  const isExpanded = expandedRows.has(c.customer_id);
                  const hasOverdue = c.bucket_31_60 > 0 || c.bucket_61_90 > 0 || c.bucket_90_plus > 0;
                  return (
                    <React.Fragment key={c.customer_id}>
                      <tr
                        className={`border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer ${hasOverdue ? 'bg-red-50/20' : ''}`}
                        onClick={() => toggleExpand(c.customer_id)}
                        data-testid={`ageing-row-${c.customer_id}`}
                      >
                        <td className="px-2 py-3 text-center">
                          {isExpanded
                            ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 inline" />
                            : <ChevronRight className="w-3.5 h-3.5 text-gray-400 inline" />
                          }
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-xs font-medium text-gray-900">{c.customer_name}</p>
                          <p className="text-[10px] text-gray-400">
                            {c.state}{c.salesperson_name ? ` | ${c.salesperson_name}` : ''}
                            {c.credit_limit > 0 && ` | Limit: ${fmtINR(c.credit_limit)}`}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-xs font-bold" style={{ color: c.total_outstanding > 0 ? '#C62828' : '#059669' }}>
                            {fmtFullINR(c.total_outstanding)}
                          </span>
                        </td>
                        {BUCKET_CONFIG.map(b => (
                          <td key={b.key} className="px-3 py-3 text-right">
                            <span className={`text-xs font-semibold ${c[b.key] > 0 ? '' : 'text-gray-300'}`}
                              style={c[b.key] > 0 ? { color: b.color } : {}}>
                              {c[b.key] > 0 ? fmtINR(c[b.key]) : '-'}
                            </span>
                          </td>
                        ))}
                        <td className="px-3 py-3 text-center">
                          {c.oldest_invoice_days > 0 ? (
                            <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                              c.oldest_invoice_days > 90 ? 'bg-red-100 text-red-700' :
                              c.oldest_invoice_days > 60 ? 'bg-orange-100 text-orange-700' :
                              c.oldest_invoice_days > 30 ? 'bg-amber-100 text-amber-700' :
                              'bg-emerald-100 text-emerald-700'
                            }`}>
                              {c.oldest_invoice_days}d
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {c.overdue_count > 0 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-100 text-red-700">
                              <Clock className="w-3 h-3" />{c.overdue_count}
                            </span>
                          ) : (
                            <span className="text-[10px] text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                      {/* Expanded Invoice Detail */}
                      {isExpanded && c.invoices && c.invoices.length > 0 && (
                        <tr data-testid={`ageing-detail-${c.customer_id}`}>
                          <td colSpan="8" className="px-0 py-0">
                            <div className="bg-gray-50/70 border-t border-b border-gray-100">
                              <table className="w-full">
                                <thead>
                                  <tr className="border-b border-gray-200">
                                    <th className="text-left px-6 py-2 text-[9px] font-semibold text-gray-400 uppercase">OC Number</th>
                                    <th className="text-left px-4 py-2 text-[9px] font-semibold text-gray-400 uppercase">Date</th>
                                    <th className="text-left px-4 py-2 text-[9px] font-semibold text-gray-400 uppercase">Status</th>
                                    <th className="text-right px-4 py-2 text-[9px] font-semibold text-gray-400 uppercase">OC Total</th>
                                    <th className="text-right px-4 py-2 text-[9px] font-semibold text-gray-400 uppercase">Outstanding</th>
                                    <th className="text-center px-4 py-2 text-[9px] font-semibold text-gray-400 uppercase">Age</th>
                                    <th className="text-center px-4 py-2 text-[9px] font-semibold text-gray-400 uppercase">Bucket</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {c.invoices.map((inv, idx) => (
                                    <tr key={idx} className="border-b border-gray-100 last:border-b-0">
                                      <td className="px-6 py-2 text-[11px] font-medium text-blue-700">{inv.oc_number}</td>
                                      <td className="px-4 py-2 text-[11px] text-gray-600">{inv.date ? new Date(inv.date + 'T00:00:00').toLocaleDateString('en-IN') : '-'}</td>
                                      <td className="px-4 py-2">
                                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                                          inv.status === 'Confirmed' ? 'bg-green-50 text-green-700' :
                                          inv.status === 'Dispatched' ? 'bg-blue-50 text-blue-700' :
                                          'bg-gray-100 text-gray-600'
                                        }`}>{inv.status}</span>
                                      </td>
                                      <td className="px-4 py-2 text-[11px] text-gray-700 text-right">{fmtFullINR(inv.total)}</td>
                                      <td className="px-4 py-2 text-[11px] font-bold text-right" style={{ color: '#C62828' }}>{fmtFullINR(inv.outstanding)}</td>
                                      <td className="px-4 py-2 text-center">
                                        <span className={`text-[10px] font-semibold ${inv.age_days > 30 ? 'text-red-600' : 'text-gray-600'}`}>
                                          {inv.age_days}d
                                        </span>
                                      </td>
                                      <td className="px-4 py-2 text-center">
                                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                                          inv.bucket === '90+' ? 'bg-red-100 text-red-700' :
                                          inv.bucket === '61-90' ? 'bg-orange-100 text-orange-700' :
                                          inv.bucket === '31-60' ? 'bg-amber-100 text-amber-700' :
                                          'bg-emerald-100 text-emerald-700'
                                        }`}>{inv.bucket}</span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-500">
          <span>
            {filtered.length} customer{filtered.length !== 1 ? 's' : ''} | Total Outstanding: INR {fmtFullINR(filtered.reduce((s, c) => s + c.total_outstanding, 0))}
          </span>
          <span className="text-gray-400">Click a row to expand invoice details</span>
        </div>
      </div>
    </div>
  );
};

export default PaymentAgeingReport;
